import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Outlet, useSearchParams, Navigate } from "react-router-dom";
import "./dashboard.css";
import "./dashboard_v2.css";
import menusConfig from "../utils/Menus";
import { setAuthToken } from "../utils/auth";
import { useUserContext } from "../utils/UserContext";
import { potAccedirAgenda, potAccedirRutaDashboard, rutaIniciSessio } from "../utils/Permisos";
import { getNomEmpresaVisible } from "../utils/Utilitats";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAddressBook, faPowerOff, faPrint } from "@fortawesome/free-solid-svg-icons";
import AgendaGlobus from "./dashboard/AgendaGlobus";

const LOGO_EMPRESA = `${process.env.PUBLIC_URL || ""}/icon-192.png`;

function rutaCoincideix(path, pathname) {
  if (!path) return false;
  return pathname === path || pathname.startsWith(`${path}/`);
}

function menuTeRutaActiva(menu, pathname) {
  if (rutaCoincideix(menu.path, pathname)) return true;
  return (menu.children || []).some((fill) => rutaCoincideix(fill.path, pathname));
}

function inicials(nom) {
  const parts = String(nom || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isPestanyaNova = searchParams.get("PestanyaNova") === "true";
  const navRef = useRef(null);

  const {
    rolUsuari,
    empresaSeleccionada,
    esSuperUsuari,
    NomUsuari,
    profileReady,
    clearSession,
    setEmpresaSeleccionada,
  } = useUserContext();

  const [navObert, setNavObert] = useState(false);
  const [menuObert, setMenuObert] = useState(null);

  useEffect(() => {
    setNavObert(false);
    setMenuObert(null);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setNavObert(false);
      setMenuObert(null);
    };
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setNavObert(false);
        setMenuObert(null);
      }
    };
    const onResize = () => {
      if (window.innerWidth > 1400) setNavObert(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (!navObert) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [navObert]);

  const tancarMenus = () => {
    setNavObert(false);
    setMenuObert(null);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleMenuClick = (item) => {
    if (!item?.path) return;
    tancarMenus();
    navigate(item.path);
  };

  const handleLogout = () => {
    tancarMenus();
    setAuthToken(null);
    clearSession();
    navigate("/");
  };

  const handleOpenEmpresaSelector = () => {
    tancarMenus();
    navigate("/dashboard/empresa");
  };

  const anarAInici = () => {
    tancarMenus();
    navigate(
      rutaIniciSessio({
        rol: rolUsuari,
        esSuperUsuari,
        empresa: empresaSeleccionada,
      })
    );
  };

  if (!profileReady) {
    return (
      <div className="Dashboard">
        <nav className="dv2-nav" aria-label="Carregant">
          <img src={LOGO_EMPRESA} alt="" className="dv2-nav-logo" width="32" height="32" />
          <span className="dv2-nav-brand-name">Carregant…</span>
        </nav>
        <div className="content" />
      </div>
    );
  }

  if (!potAccedirRutaDashboard(location.pathname, rolUsuari, { esSuperUsuari })) {
    return (
      <Navigate
        to={rutaIniciSessio({
          rol: rolUsuari,
          esSuperUsuari,
          empresa: empresaSeleccionada,
        })}
        replace
      />
    );
  }

  const rol = Number(rolUsuari);
  const baseMenus = menusConfig
    .filter((m) => m.roles?.includes(rol))
    .filter((m) => m.name !== "Empresa");

  const visibleMenus = empresaSeleccionada
    ? baseMenus
        .map((m) => {
          if (!m.children?.length) return m;
          const children = m.children.filter((sub) => sub.roles?.includes(rol));
          if (children.length === 1 && !m.path) {
            return { name: m.name, path: children[0].path, roles: children[0].roles };
          }
          return { ...m, children };
        })
        .filter((m) => m.path || (m.children && m.children.length > 0))
    : [];

  const nomEmpresa =
    getNomEmpresaVisible(empresaSeleccionada) ||
    (empresaSeleccionada?.IdEmpresa ? `Empresa #${empresaSeleccionada.IdEmpresa}` : "");

  const renderMenus = () =>
    visibleMenus.map((menu) => {
      const hasChildren = Array.isArray(menu.children) && menu.children.length > 0;
      const actiu = menuTeRutaActiva(menu, location.pathname);

      if (hasChildren) {
        const obert = menuObert === menu.name;
        return (
          <div key={menu.name} className={`dv2-nav-dropdown${obert ? " is-open" : ""}`}>
            <button
              className={`dv2-nav-item${actiu ? " is-active" : ""}`}
              type="button"
              aria-expanded={obert}
              aria-haspopup="true"
              onClick={() => setMenuObert((prev) => (prev === menu.name ? null : menu.name))}
            >
              {menu.name}
              <span className="dv2-nav-caret" aria-hidden="true">
                {obert ? "▴" : "▾"}
              </span>
            </button>
            <div className="dv2-nav-menu" role="menu">
              {menu.children.map((sub) => (
                <button
                  key={sub.path || sub.name}
                  className={`dv2-nav-subitem${rutaCoincideix(sub.path, location.pathname) ? " is-active" : ""}`}
                  onClick={() => handleMenuClick(sub)}
                  type="button"
                  role="menuitem"
                >
                  {sub.name}
                </button>
              ))}
            </div>
          </div>
        );
      }

      return (
        <button
          key={menu.path || menu.name}
          className={`dv2-nav-item${actiu ? " is-active" : ""}`}
          onClick={() => handleMenuClick(menu)}
          type="button"
        >
          {menu.name}
        </button>
      );
    });

  const accionsGlobals = (
    <div className="dv2-nav-right">
      {(nomEmpresa || NomUsuari) && (
        <span className="dv2-nav-user" title="Sessió activa">
          <span className="dv2-nav-avatar" aria-hidden="true">
            {inicials(NomUsuari)}
          </span>
          <span className="dv2-nav-user-text">
            {NomUsuari ? <span className="dv2-nav-user-name">{NomUsuari}</span> : null}
            {nomEmpresa ? <span className="dv2-nav-user-empresa">{nomEmpresa}</span> : null}
          </span>
        </span>
      )}

      {esSuperUsuari && (
        <button
          className={empresaSeleccionada ? "dv2-nav-icon" : "dv2-btn dv2-btn--primary dv2-btn--compact"}
          onClick={handleOpenEmpresaSelector}
          type="button"
          title={empresaSeleccionada ? "Canvia d’empresa" : "Selecciona empresa"}
        >
          {empresaSeleccionada ? "🏢" : "Selecciona empresa"}
        </button>
      )}

      <AgendaGlobus />

      {empresaSeleccionada && potAccedirAgenda(rolUsuari) && (
        <button
          className="dv2-nav-icon"
          onClick={() => {
            tancarMenus();
            navigate("/dashboard/contactes");
          }}
          type="button"
          title="Cercar contactes"
          aria-label="Cercar contactes"
        >
          <FontAwesomeIcon icon={faAddressBook} />
        </button>
      )}

      <button
        className="dv2-nav-icon"
        onClick={handlePrint}
        type="button"
        title="Imprimir"
        aria-label="Imprimir"
      >
        <FontAwesomeIcon icon={faPrint} />
      </button>

      <button
        className="dv2-nav-icon dv2-nav-logout"
        onClick={handleLogout}
        type="button"
        title="Tancar sessió"
        aria-label="Tancar sessió"
      >
        <FontAwesomeIcon icon={faPowerOff} />
      </button>
    </div>
  );

  return (
    <div className="Dashboard">
      {!isPestanyaNova ? (
          <nav
            ref={navRef}
            className={`dv2-nav${navObert ? " is-open" : ""}`}
            aria-label="Menú principal"
          >
            {visibleMenus.length > 0 && (
              <button
                className="dv2-nav-burger"
                type="button"
                aria-label={navObert ? "Tancar menú" : "Obrir menú"}
                aria-expanded={navObert}
                onClick={() => {
                  setNavObert((v) => !v);
                  setMenuObert(null);
                }}
              >
                <span className="dv2-nav-burger-line" />
                <span className="dv2-nav-burger-line" />
                <span className="dv2-nav-burger-line" />
              </button>
            )}

            <button className="dv2-nav-brand" type="button" onClick={anarAInici} title="Inici" aria-label="Inici">
              <img
                src={LOGO_EMPRESA}
                alt=""
                className="dv2-nav-logo"
                width="32"
                height="32"
              />
            </button>

            <div className="dv2-nav-list">{renderMenus()}</div>

            {accionsGlobals}

            {navObert && (
              <button
                type="button"
                className="dv2-nav-scrim"
                aria-label="Tancar menú"
                onClick={tancarMenus}
              />
            )}
          </nav>
      ) : (
        <nav className="dv2-nav dv2-nav--pestanya" aria-label="Pestanya">
          <span className="dv2-nav-brand" aria-hidden="true">
            <img src={LOGO_EMPRESA} alt="" className="dv2-nav-logo" width="32" height="32" />
          </span>
          <div className="dv2-nav-right">
            <button className="dv2-btn dv2-btn--ghost" onClick={() => window.close()} type="button">
              Tancar
            </button>
          </div>
        </nav>
      )}

      <div className="content">
        <Outlet
          context={{
            onEmpresaSelect: (empresa) => {
              setEmpresaSeleccionada(empresa);
              try {
                if (empresa) localStorage.setItem("empresaSeleccionada", JSON.stringify(empresa));
                else localStorage.removeItem("empresaSeleccionada");
              } catch {}
              navigate(
                rutaIniciSessio({
                  rol: rolUsuari,
                  esSuperUsuari,
                  empresa,
                })
              );
            },
          }}
        />
      </div>
    </div>
  );
}

export default Dashboard;
